import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Autocomplete,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';
import { endOfDay, isSameDay } from 'date-fns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { treatmentSheetApi, deaLogsApi } from '../../utils/api';
import api from '../../utils/api';
import { useRBAC } from '../../auth/useRBAC';
import type {
  TreatmentCategory,
  TreatmentFrequency,
  CreateOrderPayload,
  ControlledSubstance,
  TreatmentOrder,
} from '../../types/treatmentSheet';
import { Chip } from '@mui/material';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS: Array<{ value: TreatmentCategory; label: string }> = [
  { value: 'FLUID', label: 'Fluid' },
  { value: 'MEDICATION', label: 'Medication' },
  { value: 'PROCEDURE', label: 'Procedure' },
  { value: 'MONITORING', label: 'Monitoring' },
  { value: 'NUTRITION', label: 'Nutrition' },
  { value: 'OTHER', label: 'Other' },
];

const FREQUENCY_OPTIONS: Array<{ value: TreatmentFrequency; label: string }> = [
  { value: 'Q1H', label: 'Every 1h' },
  { value: 'Q2H', label: 'Every 2h' },
  { value: 'Q4H', label: 'Every 4h' },
  { value: 'Q6H', label: 'Every 6h' },
  { value: 'Q8H', label: 'Every 8h' },
  { value: 'Q12H', label: 'Every 12h' },
  { value: 'SID', label: 'Once daily' },
  { value: 'BID', label: 'Twice daily' },
  { value: 'TID', label: 'Three times daily' },
  { value: 'QID', label: 'Four times daily' },
  { value: 'PRN', label: 'As needed' },
  { value: 'CRI', label: 'Continuous (CRI)' },
  { value: 'ONCE', label: 'Once' },
];

interface MedicationOption {
  id: string;
  name: string;
  type?: string;
}

// ─── Default form state ───────────────────────────────────────────────────────

interface FormState {
  name: string;
  category: TreatmentCategory;
  dose: string;
  route: string;
  frequency: TreatmentFrequency;
  startAt: Date | null;
  specialInstructions: string;
  autoBill: boolean;
  /** Raw text of the optional unit-price field ('' = let the server use the catalog). */
  unitPrice: string;
  controlledSubstanceId?: string;
}

const defaultForm = (): FormState => ({
  name: '',
  category: 'MEDICATION',
  dose: '',
  route: '',
  frequency: 'BID',
  startAt: new Date(),
  specialInstructions: '',
  autoBill: true,
  unitPrice: '',
  controlledSubstanceId: undefined,
});

const SCHEDULE_LABEL: Record<string, string> = {
  SCHEDULE_II: 'C-II', SCHEDULE_III: 'C-III', SCHEDULE_IV: 'C-IV', SCHEDULE_V: 'C-V',
};
const SCHEDULE_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  SCHEDULE_II: 'error', SCHEDULE_III: 'warning', SCHEDULE_IV: 'info', SCHEDULE_V: 'default',
};

// ─── AddOrderSlideOver ────────────────────────────────────────────────────────

interface AddOrderSlideOverProps {
  sheetId: string;
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  /** When set, the slide-over edits this existing order instead of creating one. */
  editingOrder?: TreatmentOrder | null;
  /** Whether the clinic has hospital auto-bill switched on — changes what the toggle can honestly claim. */
  autoBillLive?: boolean;
}

const formFromOrder = (order: TreatmentOrder): FormState => ({
  name: order.name,
  category: order.category,
  dose: order.dose ?? '',
  route: order.route ?? '',
  frequency: order.frequency,
  startAt: order.startAt ? new Date(order.startAt) : new Date(),
  specialInstructions: order.specialInstructions ?? '',
  autoBill: order.autoBill ?? true,
  unitPrice: order.unitPrice != null ? String(order.unitPrice) : '',
  controlledSubstanceId: order.controlledSubstanceId ?? undefined,
});

const AddOrderSlideOver: React.FC<AddOrderSlideOverProps> = ({
  sheetId,
  open,
  onClose,
  onAdded,
  editingOrder,
  autoBillLive,
}) => {
  const theme = useTheme();
  const { hasRole } = useRBAC();
  /**
   * ⚠ KEPT AS A ROLE BECAUSE THE SERVER GATES ON THAT ROLE — corrected 2026-09-21.
   *
   * This comment used to call the rule UI-only and warn that "dropping it would let an office
   * manager write a C-II order". That was wrong in the way a decorator walk is always wrong here:
   * the API refuses an ADMIN controlled-substance order in the SERVICE, where no `@Roles()` scan
   * can see it — `treatment-order.service.ts:102-103` ("Admins cannot create controlled-substance
   * orders") on create, and `:238-239` on duplicate-day, which refuses the whole day. So this
   * check is a MIRROR of a live server rule, and dropping it would produce a picker whose save
   * 403s, not an escalation.
   *
   * The decorators are still only half the story and that half is unchanged: the order write is
   * `manage:medical_records` (treatment-order.controller.ts :39-40), the substance list is
   * `view:medical_records` (dea-logs.controller.ts :27-28), and `manage:controlled_substances` is
   * a slug NO route enforces. Read the SERVICE before calling a role check here a UI invention.
   *
   * To retire the rule properly, change it on the server first — the web follows.
   */
  const isAdmin = hasRole('ADMIN');
  const isEditing = !!editingOrder;
  const [form, setForm] = useState<FormState>(defaultForm());
  const [error, setError] = useState<string | null>(null);
  // The catalog price resolved for the name currently typed, and whether the
  // vet has overridden it. Once they type their own price we stop touching the
  // field — an auto-fill that overwrites a deliberate number is worse than none.
  const [catalogPrice, setCatalogPrice] = useState<number | null>(null);
  const [priceTouched, setPriceTouched] = useState(false);

  // Sync the form to the order being edited (or reset for a fresh add) whenever
  // the drawer opens or the target order changes.
  useEffect(() => {
    if (!open) return;
    setForm(editingOrder ? formFromOrder(editingOrder) : defaultForm());
    setError(null);
    setCatalogPrice(null);
    // An order being EDITED already carries its snapshot price, so treat that as
    // the vet's number and leave it alone.
    setPriceTouched(!!editingOrder?.unitPrice);
  }, [open, editingOrder]);

  // Show what this order WILL be billed at while it is still being written.
  // The server has always snapshotted a catalog price on create, but the drawer
  // never asked for it, so the price first became visible after saving and
  // reopening — the vet wrote the order blind. Same lookup the create path runs.
  useEffect(() => {
    if (!open) return;
    const name = form.name.trim();
    if (!name) {
      setCatalogPrice(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      treatmentSheetApi
        .previewOrderPrice(name, form.category)
        .then(({ unitPrice }) => {
          if (cancelled) return;
          setCatalogPrice(unitPrice);
          // Only fill a field the vet has not made their own.
          if (unitPrice != null && !priceTouched) {
            setForm((prev) => ({ ...prev, unitPrice: String(unitPrice) }));
          }
        })
        .catch(() => {
          // A pricing hiccup must never block writing a clinical order.
          if (!cancelled) setCatalogPrice(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, form.name, form.category, priceTouched]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Fetch medication/procedure options for autocomplete
  const { data: medicationOptions = [] } = useQuery<MedicationOption[]>(
    ['clinic-medications'],
    () => api.get('/medications').then((r) => r.data),
    {
      enabled: open,
      staleTime: 5 * 60 * 1000,
      onError: () => {}, // silently fail — user can still type freely
    },
  );

  const { data: procedureOptions = [] } = useQuery<MedicationOption[]>(
    ['clinic-procedures'],
    () => api.get('/procedures').then((r) => r.data),
    {
      enabled: open,
      staleTime: 5 * 60 * 1000,
      onError: () => {},
    },
  );

  const { data: controlledSubstances = [] } = useQuery<ControlledSubstance[]>(
    ['controlled-substances'],
    () => deaLogsApi.listSubstances(false),
    { enabled: open && !isAdmin, staleTime: 5 * 60 * 1000, onError: () => {} },
  );

  const selectedControlled = controlledSubstances.find(
    (cs) => cs.id === form.controlledSubstanceId,
  );

  const allNameOptions: string[] = [
    ...medicationOptions.map((m) => m.name),
    ...procedureOptions.map((p) => p.name),
  ].filter((v, i, arr) => arr.indexOf(v) === i);

  const mutation = useMutation(
    (payload: CreateOrderPayload) =>
      isEditing
        ? treatmentSheetApi.updateOrder(editingOrder!.id, payload)
        : treatmentSheetApi.createOrder(sheetId, payload),
    {
      onSuccess: () => {
        setForm(defaultForm());
        setError(null);
        onAdded();
      },
      onError: () =>
        setError(
          `Failed to ${isEditing ? 'save' : 'add'} order. Please check your inputs and try again.`,
        ),
    },
  );

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError('Order name is required.');
      return;
    }
    if (!form.startAt) {
      setError('Start date/time is required.');
      return;
    }

    // Day-scoping: an order lives within its start day (endAt = local end of
    // that day); the next day starts empty until "Duplicate to today" carries
    // it forward. On edit, endAt is only recomputed when startAt moved to a
    // different day — otherwise the stored endAt stands.
    const startChangedDay =
      isEditing && editingOrder?.startAt
        ? !isSameDay(new Date(editingOrder.startAt), form.startAt)
        : !isEditing;
    const priceText = form.unitPrice.trim();
    const parsedPrice = priceText === '' ? undefined : Number(priceText);
    if (parsedPrice !== undefined && (!Number.isFinite(parsedPrice) || parsedPrice < 0)) {
      setError('Unit price must be a number of 0 or more (leave it blank to use the catalog price).');
      return;
    }
    const payload: CreateOrderPayload = {
      name: form.name.trim(),
      category: form.category,
      dose: form.dose.trim() || undefined,
      route: form.route.trim() || undefined,
      frequency: form.frequency,
      startAt: form.startAt.toISOString(),
      ...(startChangedDay ? { endAt: endOfDay(form.startAt).toISOString() } : {}),
      specialInstructions: form.specialInstructions.trim() || undefined,
      autoBill: form.autoBill,
      // Omitted (blank) → the server snapshots the catalog price for the name.
      ...(parsedPrice !== undefined ? { unitPrice: parsedPrice } : {}),
      // The controlled-substance link is set at creation only; the update
      // endpoint does not accept it, so omit it when editing.
      ...(isEditing ? {} : { controlledSubstanceId: form.controlledSubstanceId }),
    };

    setError(null);
    mutation.mutate(payload);
  };

  const handleClose = () => {
    setForm(defaultForm());
    setError(null);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 480,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {isEditing ? 'Edit Treatment Order' : 'Add Treatment Order'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form body */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Controlled-substance link is read-only once an order exists (the
                update endpoint does not change it). Surface it as a chip. */}
            {isEditing && editingOrder?.controlledSubstance && (
              <Chip
                label={`Controlled: ${editingOrder.controlledSubstance.genericName} · ${editingOrder.controlledSubstance.strength} · ${SCHEDULE_LABEL[editingOrder.controlledSubstance.deaSchedule] ?? editingOrder.controlledSubstance.deaSchedule}`}
                color={SCHEDULE_COLOR[editingOrder.controlledSubstance.deaSchedule] ?? 'default'}
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              />
            )}
            {/* Controlled substance picker (DEA) — add mode only, hidden for admins */}
            {!isEditing && !isAdmin && (
            <Autocomplete
              options={controlledSubstances}
              value={selectedControlled ?? null}
              getOptionLabel={(opt) => `${opt.genericName}${opt.brandName ? ` (${opt.brandName})` : ''} · ${opt.strength}`}
              onChange={(_, opt) => {
                if (opt) {
                  set('controlledSubstanceId', opt.id);
                  set('name', opt.brandName ?? opt.genericName);
                  set('category', 'MEDICATION');
                } else {
                  set('controlledSubstanceId', undefined);
                }
              }}
              renderOption={(props, opt) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip
                      label={SCHEDULE_LABEL[opt.deaSchedule]}
                      color={SCHEDULE_COLOR[opt.deaSchedule]}
                      size="small"
                    />
                    <span>{opt.genericName}{opt.brandName ? ` (${opt.brandName})` : ''}</span>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {opt.strength} · {opt.dosageForm.toLowerCase()}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Controlled Substance (optional)"
                  placeholder="Pick if administering a DEA-scheduled drug"
                  helperText={selectedControlled ? `Witness will be required when this dose is administered. Schedule ${selectedControlled.deaSchedule.replace('SCHEDULE_', '')}.` : undefined}
                />
              )}
            />
            )}

            {/* Order name with autocomplete */}
            <Autocomplete
              freeSolo
              options={allNameOptions}
              value={form.name}
              onInputChange={(_, v) => set('name', v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Order Name *"
                  placeholder="e.g. Banamine, LRS Fluids, Bandage Change"
                />
              )}
            />

            {/* Category chip group */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                Category
              </Typography>
              <ToggleButtonGroup
                value={form.category}
                exclusive
                onChange={(_, v) => v && set('category', v as TreatmentCategory)}
                size="small"
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <ToggleButton
                    key={opt.value}
                    value={opt.value}
                    sx={{ borderRadius: '16px !important', px: 1.5, py: 0.5, fontSize: 12 }}
                  >
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Dose + Route */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Dose"
                value={form.dose}
                onChange={(e) => set('dose', e.target.value)}
                placeholder="e.g. 1.1 mg/kg"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Route"
                value={form.route}
                onChange={(e) => set('route', e.target.value)}
                placeholder="e.g. IV, PO, IM"
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Frequency segmented */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                Frequency
              </Typography>
              <ToggleButtonGroup
                value={form.frequency}
                exclusive
                onChange={(_, v) => v && set('frequency', v as TreatmentFrequency)}
                size="small"
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <ToggleButton
                    key={opt.value}
                    value={opt.value}
                    sx={{ borderRadius: '16px !important', px: 1.25, py: 0.5, fontSize: 11 }}
                  >
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Start date/time */}
            <DateTimePicker
              label="Start Date/Time *"
              value={form.startAt}
              onChange={(v) => set('startAt', v)}
              slotProps={{ textField: { fullWidth: true } }}
            />


            <Divider />

            {/* Special instructions */}
            <TextField
              label="Special Instructions"
              value={form.specialInstructions}
              onChange={(e) => set('specialInstructions', e.target.value)}
              multiline
              minRows={2}
              placeholder="Additional instructions for staff..."
            />

            {/* Unit price — what auto-bill posts per administration (PR6a) */}
            <TextField
              label="Unit price ($)"
              type="number"
              size="small"
              value={form.unitPrice}
              onChange={(e) => {
                setPriceTouched(true);
                set('unitPrice', e.target.value);
              }}
              inputProps={{ min: 0, step: 0.01 }}
              placeholder="Catalog price"
              helperText={
                catalogPrice != null && !priceTouched
                  ? `From your price list for "${form.name.trim()}" — change it to override.`
                  : catalogPrice == null && form.name.trim() !== ''
                    ? 'No price list entry for this name — enter one, or this order bills nothing.'
                    : 'Leave blank to use the service catalog price for this name.'
              }
              sx={{ maxWidth: 260 }}
            />
            {/* Future tense on purpose: hospital invoicing is not switched on
                yet, so nothing posts today — but the price is captured now and
                is what WILL post, so a missing one is worth flagging while the
                vet is still in the form. */}
            {form.autoBill && !(Number(form.unitPrice) > 0) && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {form.unitPrice.trim() === ''
                  ? 'No unit price entered. If the price list has no price for this name, administrations of this order will post no charge.'
                  : 'A $0 unit price means administrations of this order will post a no-charge line.'}
              </Alert>
            )}

            {/* Auto-bill toggle. Stays INTERACTIVE even though hospital
                invoicing isn't live yet: this switch is how a vet marks an
                order NON-billable, and the flag is stored on the order. Grey
                it out and every order written in the meantime keeps the
                default (on), so all of them would start charging the day
                invoicing is switched on — including the ones nobody wanted
                billed. The caption carries the honesty instead. */}
            <FormControlLabel
              control={
                <Switch
                  checked={form.autoBill}
                  onChange={(e) => set('autoBill', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Auto-bill to invoice</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {autoBillLive
                      ? "Adds to this admission's invoice when administered."
                      : 'Adds to the client\'s invoice when administered. Takes effect once hospital invoicing is switched on — until then, bill this stay from Billing.'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Button fullWidth variant="outlined" onClick={handleClose} disabled={mutation.isLoading}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={mutation.isLoading || !form.name.trim()}
            startIcon={
              mutation.isLoading ? <CircularProgress size={16} /> : <AddIcon />
            }
          >
            {isEditing ? 'Save Changes' : 'Add to Sheet'}
          </Button>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
};

export default AddOrderSlideOver;
